import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useCallback } from "react";
import StampFound from "../components/StampFound";
import StampNotFound from "../components/StampNotFound";
import { processFile } from "../utils/processFile";
import { verifyHashWorkerVerify } from "../utils/verifyHashWorkerVerify";
import VerifyContentId from "../components/VerifyContentId";
import VerifyFile from "../components/VerifyFile";
import VerifyUserFound from "../components/VerifyUserFound";
import VerifyCollection from "../components/VerifyCollection";
import Cookies from "js-cookie";
import TriggerHelp from "../components/TriggerHelp";
import NeedHelp from "../components/NeedHelp";
import LearnMore from "../components/landing-components/LearnMore";
import Landing from "../components/landing-components/Index";
import Loading from "../components/Loading";
import { getVerifiedFileByHash, URLs } from "../utils/apis";
import CollectionFound from "../components/CollectionFound";
import CollectionNotFound from "../components/CollectionNotFound";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { Box, Flex, Text, useMantineTheme } from "@mantine/core";
import CustomButton from "../components/mantineCustomComponents/CustomButton";
import CustomTooltip from "../components/mantineCustomComponents/CustomTooltip";
import { useStylesButton } from "../utils/mantine/useStylesButton";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import CustomAnchor from "../components/mantineCustomComponents/CustomAnchor";
import FileFound from "../components/FileFound";
import VerifiedFileError from "../components/VerifiedFileError";

export const VERIFY_QUERY_PARAMS = [
  "cid",
  "transaction_hash",
  "collection_cid",
  "collection_id",
];

export const clearVerifyQueryParams = (url) => {
  const hasVerifyQueryParams = VERIFY_QUERY_PARAMS.some((param) =>
    url.searchParams.has(param),
  );

  if (!hasVerifyQueryParams) {
    return false;
  }

  VERIFY_QUERY_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });
  return true;
};

const VerifyForm = ({ staticBasePath, djangoContext }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css classes
  const { classes: classesPage } = useStylesPage();

  const { classes: buttonClasses } = useStylesButton();

  const { classes: tooltipClasses } = useStylesTooltip();

  const [helpIsActive, setHelpIsActive] = useState(() => {
    const cookieValue = Cookies.get("helpIsActive");
    return cookieValue !== undefined ? JSON.parse(cookieValue) : true;
  });

  const handleHelpTriggerClick = () => {
    const newHelpIsActive = !helpIsActive;
    setHelpIsActive(newHelpIsActive);
    Cookies.set("helpIsActive", JSON.stringify(newHelpIsActive));
  };

  const [verificationMethod, setVerificationMethod] = useState(() => {
    const cookieVerificationMethod = Cookies.get("verificationMethod");

    let method;
    if (cookieVerificationMethod !== undefined) {
      method = JSON.parse(cookieVerificationMethod);
    }
    const allowedMethods = ["hash", "user", "collection"];

    return allowedMethods.includes(method) ? method : "file";
  });
  const [objectHash, setObjectHash] = useState(null);
  const [userData, setUserData] = useState("");
  const [stampData, setStampData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentScreen, setCurrentScreen] = useState("verify"); // new state to manage screen
  const [isValid, setIsValid] = useState(true);
  const [contentIdTouched, setContentIdTouched] = useState(false);
  const regex = /^(0x)?[0-9a-fA-F]{64}$/;
  const [isAuthenticated, setIsAuthenticated] = useState(
    djangoContext.userIsAuthenticated,
  );

  // Check authentication dynamically on mount
  useEffect(() => {
    const checkAuth = async () => {
      const { checkAuthentication } = await import("../utils/apis");
      await checkAuthentication(setIsAuthenticated);
    };
    checkAuth();
  }, []);
  useEffect(() => {
    const handleLocationChange = () => {
      const params = new URLSearchParams(window.location.search);
      const cidParam = params.get("cid");
      const targetStamp = {
        transactionHash: params.get("transaction_hash"),
        collectionCid:
          params.get("collection_cid") || params.get("collection_id"),
      };
      const checkValid = regex.test(cidParam) || cidParam === "";
      setIsValid(checkValid);
      if (checkValid) {
        setObjectHash(cidParam);
        handleVerifyHashWorkerVerify(cidParam, undefined, targetStamp);
      }
    };

    window.addEventListener("popstate", handleLocationChange);
    handleLocationChange();
    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  const handleDefaultScreen = () => {
    setError(false);
    setCurrentScreen("");
    setObjectHash("");

    const url = new URL(window.location);
    if (clearVerifyQueryParams(url)) {
      window.history.pushState({}, "", url);
    }
  };

  const handleVerificationMethodChange = (method) => {
    setVerificationMethod(method);
    Cookies.set("verificationMethod", JSON.stringify(method));

    // Update URL with method parameter
    const url = new URL(window.location);
    url.searchParams.set("method", method);

    // Remove collectionMethod parameter when switching to non-collection methods
    if (method !== "collection") {
      url.searchParams.delete("collectionMethod");
    }

    window.history.pushState({}, "", url);
  };

  const handleFileInput = (event) => {
    event.preventDefault();
    const file = event.target.files?.[0] || event.dataTransfer?.files?.[0];
    if (file) {
      handleProcessFile(file);
    }
  };

  const handleHashChange = (event) => {
    const { value } = event.target;
    setContentIdTouched(true);
    setIsValid(regex.test(value) || value === "");
    setObjectHash(value);
  };

  const handleUserDataChange = (event) => {
    const { value } = event.target;
    setUserData(value);
  };

  const handleSubmit = () => {
    if (objectHash !== "") {
      handleVerifyHashWorkerVerify(objectHash, undefined);
    }
  };

  const handleUserDataSubmit = () => {
    if (userData !== "") {
      const urlUserFound = new URL(
        URLs.VERIFY_USER_DATA_URL,
        window.location.origin,
      );
      urlUserFound.searchParams.set("user", userData);
      window.location.href = urlUserFound;
    }
  };

  const [allHashes, setAllHashes] = useState({});
  const handleProcessFile = (file) => {
    // get hash with web3
    processFile(
      file,
      setObjectHash,
      handleVerifyHashWorkerVerify,
      setAllHashes,
    );
  };

  const handleVerifyHashWorkerVerify = (hash, supportedHashes, targetStamp) => {
    // fetch /verify/hash/
    verifyHashWorkerVerify(
      hash,
      supportedHashes,
      setError,
      setStampData,
      setCurrentScreen,
      setObjectHash,
      setLoading,
      targetStamp,
    );
  };
  const [isNearBottom, setIsNearBottom] = useState(false);
  const handleScroll = () => {
    const rbMainContent = document.querySelector(".main-content");

    if (!rbMainContent) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const contentOffsetTop = rbMainContent.offsetTop;
    const contentHeight = rbMainContent.offsetHeight;
    const windowHeight = window.innerHeight;
    const remainingScroll =
      contentOffsetTop + contentHeight - (scrollTop + windowHeight);

    if (
      (remainingScroll <= 80 && window.innerWidth <= 639) ||
      (remainingScroll <= 8 && window.innerWidth >= 640)
    ) {
      setIsNearBottom(true);
    } else {
      setIsNearBottom(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const rbRoot = document.querySelector(".rb-root");
    if (currentScreen === "found" || error) {
      rbRoot.classList.remove("full-screen-min-height");
    }

    return () => {
      rbRoot.classList.remove("full-screen-min-height");
    };
  }, [currentScreen, error]);

  const landingSectionRef = useRef(null);

  // Collection Found
  const [showCollectionFound, setShowCollectionFound] = useState(false);

  const handleShowCollectionFound = useCallback((bool) => {
    setShowCollectionFound(bool);
  }, []);

  const [contentCollectionFound, setContentCollectionFound] = useState([]);

  const handleContentCollectionFound = useCallback((array) => {
    setContentCollectionFound(array);
  }, []);

  const [fileName, setFileName] = useState("");

  const handleFileName = useCallback((name) => {
    setFileName(name);
  }, []);

  const [fileType, setFileType] = useState("");

  const handleFileType = useCallback((type) => {
    setFileType(type);
  }, []);

  const [errorMessage, setErrorMessage] = useState("");

  const handleErrorMessage = useCallback((message) => {
    setErrorMessage(message);
  }, []);

  const [loadingGetVerifiedFileByHash, setLoadingGetVerifiedFileByHash] =
    useState(false); // Loading create file link

  //Link anchors verify tabs
  useEffect(() => {
    const hash = window.location.hash.substring(1); // Get the part after #
    const urlParams = new URLSearchParams(window.location.search);
    const verifyParam = urlParams.get("method");

    // Define valid verification methods
    const validMethods = ["file", "hash", "user", "collection"];

    // Check if the hash or query param contains a valid method
    const method = validMethods.find(
      (m) => hash.includes(m) || verifyParam === m,
    );
    if (method) {
      handleVerificationMethodChange(method);
    }

    // Handle collection method parameters (archive, s3-link, s3-credentials)
    const collectionMethodParam = urlParams.get("collectionMethod");
    const validCollectionMethods = ["archive", "s3-link", "s3-credentials"];

    if (
      collectionMethodParam &&
      validCollectionMethods.includes(collectionMethodParam)
    ) {
      // Set collection as the main method if not already set
      if (verificationMethod !== "collection") {
        handleVerificationMethodChange("collection");
      }
      // The specific collection method will be handled by VerifyCollection component
    }

    const params = new URLSearchParams(window.location.search);

    const hasRelevantParam = params.has("link-id");

    if (hasRelevantParam) {
      const fetchFileData = async () => {
        setLoadingGetVerifiedFileByHash(true);

        const fileHash = params.get("link-id");

        // 1. Fetch file data based on hash
        const response = await getVerifiedFileByHash(fileHash);

        if (!response || !response.data) {
          setStampData({ error: "Failed to fetch file data" });
          setLoadingGetVerifiedFileByHash(false);
          return;
        }

        if (response.data.error) {
          setStampData({ error: response.data.error });
          setLoadingGetVerifiedFileByHash(false);
          return;
        }

        // 2. Set stamp data with the first stamp and display timezone
        setStampData({
          ...response.data?.stamp_list[0],
          display_timezone: response.data?.display_timezone,
        });

        setLoadingGetVerifiedFileByHash(false);
      };

      fetchFileData();

      setCurrentScreen("fileLink");
    }
  }, []);

  return (
    <>
      <title>vBase Verify App</title>
      <meta
        name="description"
        content="Use vBase Verify to quickly and easily verify stamps!"
      />
      <meta property="og:title" content="vBase Verify App" />
      <meta
        property="og:description"
        content="Use vBase Verify to quickly and easily verify stamps!"
      />

      <Flex
        className={classesPage.page + " main-content"}
        direction="column"
        align={theme.flex.align.center}
      >
        {error ? (
          <Box className={classesPage.errorMessage}>{error}</Box>
        ) : currentScreen === "found" ? (
          <>
            <StampFound
              key={stampData.id}
              data={stampData}
              staticBasePath={staticBasePath}
              handleDefaultScreen={handleDefaultScreen}
              allHashes={allHashes}
            />
          </>
        ) : currentScreen === "fileLink" && stampData ? (
          <>
            {stampData.error ? (
              <VerifiedFileError
                error={stampData.error}
                staticBasePath={staticBasePath}
                handleDefaultScreen={handleDefaultScreen}
              />
            ) : currentScreen === "fileLink" && stampData ? (
              <FileFound
                key={stampData.objectCid}
                data={stampData}
                staticBasePath={staticBasePath}
              />
            ) : null}
          </>
        ) : currentScreen === "notfound" ? (
          <StampNotFound
            hash={objectHash}
            staticBasePath={staticBasePath}
            handleDefaultScreen={handleDefaultScreen}
            verificationMethod={verificationMethod}
          />
        ) : (
          <>
            {showCollectionFound || errorMessage ? (
              <>
                {showCollectionFound && (
                  <CollectionFound
                    contentCollectionFound={contentCollectionFound}
                    staticBasePath={staticBasePath}
                    fileName={fileName}
                    fileType={fileType}
                  />
                )}

                {errorMessage && (
                  <CollectionNotFound
                    staticBasePath={staticBasePath}
                    errorMessage={errorMessage}
                  />
                )}
              </>
            ) : (
              <>
                <Flex
                  className={classesPage.screen}
                  direction="column"
                  align={theme.flex.align.center}
                >
                  {loading && (
                    <Loading
                      title="One moment! Searching for the stamp..."
                      text="Can take 20 seconds, but usually faster!"
                    />
                  )}

                  {loadingGetVerifiedFileByHash && (
                    <Loading
                      title="One moment! Searching for the stamp..."
                      text="Can take 10 seconds, but usually faster!"
                    />
                  )}

                  <Box className={classesPage.header}>
                    <Text className={classesPage.headerTitle}>
                      Verify{" "}
                      {(verificationMethod === "user" ||
                        verificationMethod === "collection") &&
                        "a"}{" "}
                      <Text component="span"> vBase</Text>
                      {verificationMethod === "user"
                        ? " User"
                        : verificationMethod === "collection"
                        ? " Collection"
                        : " Stamps"}
                    </Text>
                    <Text className={classesPage.headerText}>
                      {verificationMethod === "user"
                        ? "View stamping activity of a vBase user"
                        : verificationMethod === "collection"
                        ? "Verify the authenticity and integrity of a Collection"
                        : "Validate the author, content and timestamp for any vBase Stamp"}
                    </Text>
                    <NeedHelp
                      staticBasePath={staticBasePath}
                      linkDocs="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-verify"
                      linkVideo="https://youtu.be/nzbC3UphfGM"
                    />
                  </Box>

                  <Flex align={theme.flex.align.center} gap={theme.flex.gap.xs}>
                    <Flex className={buttonClasses.tabs}>
                      <CustomButton
                        id="verify-method-file"
                        text="File"
                        onClick={() => handleVerificationMethodChange("file")}
                        componentClasses={`rb-btn-file button ${
                          verificationMethod === "file" ? "active" : ""
                        }`}
                      />

                      <CustomButton
                        id="verify-method-hash"
                        text="Content ID"
                        onClick={() => handleVerificationMethodChange("hash")}
                        componentClasses={`rb-btn-hash button ${
                          verificationMethod === "hash" ? "active" : ""
                        }`}
                      />

                      <CustomButton
                        id="user-found"
                        text="User"
                        onClick={() => handleVerificationMethodChange("user")}
                        componentClasses={`rb-btn-user-found button ${
                          verificationMethod === "user" ? "active" : ""
                        }`}
                      />

                      <CustomButton
                        id="long-csv"
                        text="Collection"
                        onClick={() =>
                          handleVerificationMethodChange("collection")
                        }
                        componentClasses={`rb-btn-long-csv button ${
                          verificationMethod === "collection" ? "active" : ""
                        }`}
                      />
                    </Flex>
                    {helpIsActive && (
                      <CustomTooltip
                        content={
                          <Text
                            classNames={{
                              root: tooltipClasses.tooltipText,
                            }}
                          >
                            Select whether to validate the stamps associated
                            with a given File or Content ID. Or, validate the
                            Stamp activity of a user.
                          </Text>
                        }
                      ></CustomTooltip>
                    )}
                  </Flex>

                  {verificationMethod === "file" && (
                    <VerifyFile
                      handleFileInput={handleFileInput}
                      staticBasePath={staticBasePath}
                      helpIsActive={helpIsActive}
                    />
                  )}

                  {verificationMethod === "hash" && (
                    <VerifyContentId
                      isValid={isValid}
                      showValidationError={contentIdTouched && !isValid}
                      objectHash={objectHash}
                      handleHashChange={handleHashChange}
                      handleSubmit={handleSubmit}
                    />
                  )}

                  {verificationMethod === "user" && (
                    <VerifyUserFound
                      userData={userData}
                      onUserDataChange={handleUserDataChange}
                      onUserDataSubmit={handleUserDataSubmit}
                      isAuthenticated={isAuthenticated}
                    />
                  )}
                  {verificationMethod === "collection" && (
                    <VerifyCollection
                      setIsAuthenticated={setIsAuthenticated}
                      onShowCollectionFound={handleShowCollectionFound}
                      onContentCollectionFound={handleContentCollectionFound}
                      onFileName={handleFileName}
                      onErrorMessage={handleErrorMessage}
                      onFileType={handleFileType}
                      isAuthenticated={isAuthenticated}
                    />
                  )}
                  <Box className={classesPage.manyStamps}>
                    Need to verify many stamps?{" "}
                    <CustomAnchor
                      text="Check out our handy API"
                      componentClasses="link anchor"
                      href="https://docs.vbase.com/getting-started/python-quickstart"
                    ></CustomAnchor>
                  </Box>
                </Flex>
                <TriggerHelp
                  helpIsActive={helpIsActive}
                  isNearBottom={isNearBottom}
                  handleHelpTriggerClick={handleHelpTriggerClick}
                />
              </>
            )}
          </>
        )}
        {!showCollectionFound && !errorMessage && (
          <LearnMore
            staticBasePath={staticBasePath}
            landingSectionRef={landingSectionRef}
          />
        )}
      </Flex>
      {!showCollectionFound && !errorMessage && (
        <Landing
          staticBasePath={staticBasePath}
          isAuthenticated={isAuthenticated}
          landingSectionRef={landingSectionRef}
        />
      )}
    </>
  );
};
VerifyForm.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  djangoContext: PropTypes.object.isRequired,
};
export default VerifyForm;
